import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Clock, AlertTriangle } from 'lucide-react'
import { setAuthToken } from '../utils/auth'

/**
 * SessionManager - Professional SaaS-standard session timeout handling
 *
 * Features:
 * - Auto-refreshes auth token every 20 minutes
 * - Warns user 5 minutes before session expires
 * - Shows modal on session expiration (no browser alerts)
 * - Tracks user activity to extend sessions
 * - Graceful degradation on API errors
 */

interface SessionManagerProps {
  enabled?: boolean // Disable in tenant mode for public sites
}

const SessionManager: React.FC<SessionManagerProps> = ({ enabled = true }) => {
  const navigate = useNavigate()
  const [showWarning, setShowWarning] = useState(false)
  const [showExpired, setShowExpired] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null)

  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const expiryCheckIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const activityTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastActivityRef = useRef<number>(Date.now())
  const refreshFailureCountRef = useRef<number>(0)
  const lastRefreshTimeRef = useRef<number>(0)

  // Session timeout: 4 hours of inactivity (matches server-side timeout)
  const SESSION_DURATION = 4 * 60 * 60 * 1000 // 4 hours in ms
  const REFRESH_INTERVAL = 20 * 60 * 1000 // Refresh every 20 minutes (if active)
  const MAX_IDLE_BEFORE_REFRESH = 30 * 60 * 1000 // Only refresh if active in last 30 min
  const WARNING_THRESHOLD = 5 * 60 * 1000 // Warn 5 minutes before expiration
  const MAX_REFRESH_FAILURES = 3 // Allow 3 failures before considering session expired

  /**
   * Track user activity to extend session
   */
  const updateActivity = () => {
    lastActivityRef.current = Date.now()

    // Store last activity timestamp
    localStorage.setItem('last-activity', Date.now().toString())
  }

  /**
   * Refresh auth token to extend session
   */
  const refreshToken = async (): Promise<boolean> => {
    try {
      // Prevent rapid consecutive refreshes (min 1 minute between refreshes)
      const timeSinceLastRefresh = Date.now() - lastRefreshTimeRef.current
      if (timeSinceLastRefresh < 60000) {
        console.log('[SessionManager] Skipping refresh - refreshed', Math.floor(timeSinceLastRefresh / 1000), 'seconds ago')
        return true // Return true since we have a recent valid token
      }

      const token = localStorage.getItem('auth-token')
      if (!token) {
        console.log('[SessionManager] No token to refresh')
        return false
      }

      console.log('[SessionManager] Refreshing auth token...')

      // Add timeout to prevent hanging requests
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 10000) // 10 second timeout

      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        signal: controller.signal,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      clearTimeout(timeoutId)

      if (response.ok) {
        const data = await response.json()
        if (data.token) {
          setAuthToken(data.token)
          localStorage.setItem('last-activity', Date.now().toString())
          lastRefreshTimeRef.current = Date.now() // Track successful refresh time
          console.log('[SessionManager] ✅ Token refreshed successfully')
          refreshFailureCountRef.current = 0 // Reset failure counter on success
          setShowWarning(false)
          return true
        }
      }

      console.warn('[SessionManager] Token refresh failed:', response.status)
      refreshFailureCountRef.current++
      console.log(`[SessionManager] Refresh failures: ${refreshFailureCountRef.current}/${MAX_REFRESH_FAILURES}`)
      return false
    } catch (error) {
      console.error('[SessionManager] Token refresh error:', error)
      refreshFailureCountRef.current++
      console.log(`[SessionManager] Refresh failures: ${refreshFailureCountRef.current}/${MAX_REFRESH_FAILURES}`)

      // Only show expired modal after multiple consecutive failures
      if (refreshFailureCountRef.current >= MAX_REFRESH_FAILURES) {
        console.error('[SessionManager] Max refresh failures reached - session may be expired')
      }

      return false
    }
  }

  /**
   * Check if session is about to expire or has expired
   */
  const checkSessionExpiry = () => {
    const token = localStorage.getItem('auth-token')
    if (!token) return

    const lastActivity = parseInt(localStorage.getItem('last-activity') || '0')
    const timeSinceActivity = Date.now() - lastActivity
    const timeUntilExpiry = SESSION_DURATION - timeSinceActivity

    setTimeRemaining(timeUntilExpiry)

    // Session has expired
    if (timeUntilExpiry <= 0) {
      console.log('[SessionManager] Session expired')
      handleSessionExpired()
      return
    }

    // Warn if session expiring soon
    if (timeUntilExpiry <= WARNING_THRESHOLD && !showWarning) {
      console.log('[SessionManager] Session expiring soon, showing warning')
      setShowWarning(true)
    }
  }

  /**
   * Handle session expiration
   */
  const handleSessionExpired = () => {
    // Clear all intervals
    if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current)
    if (expiryCheckIntervalRef.current) clearInterval(expiryCheckIntervalRef.current)

    // Show expiration modal
    setShowExpired(true)
    setShowWarning(false)

    // Clear auth data including last-activity so a fresh login doesn't immediately re-expire
    localStorage.removeItem('auth-token')
    localStorage.removeItem('last-activity')
    sessionStorage.removeItem('user-session')
    document.cookie = 'auth-token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'

    // Store session timeout flag for login page
    sessionStorage.setItem('sessionTimedOut', 'true')
  }

  /**
   * Extend session when user clicks "Stay Logged In"
   */
  const handleExtendSession = async () => {
    const success = await refreshToken()
    if (success) {
      setShowWarning(false)
      updateActivity()
    } else {
      // Only expire if we've had multiple failures or session is actually expired
      if (refreshFailureCountRef.current >= MAX_REFRESH_FAILURES) {
        handleSessionExpired()
      } else {
        // Try again - single failures shouldn't kill the session
        setShowWarning(false)
        updateActivity() // Keep session alive locally
      }
    }
  }

  /**
   * Handle user logout from expired modal
   */
  const handleLogout = () => {
    setShowExpired(false)
    navigate('/login')
  }

  /**
   * Setup session management
   */
  useEffect(() => {
    if (!enabled) return

    const token = localStorage.getItem('auth-token')
    if (!token) return

    // Don't manage sessions on login/auth pages - user is authenticating
    const currentPath = window.location.pathname
    if (currentPath.includes('/login') || currentPath.includes('/auth/')) {
      console.log('[SessionManager] Skipping on login/auth pages')
      return
    }

    console.log('[SessionManager] Starting session management')

    // Initialize last activity if not set
    if (!localStorage.getItem('last-activity')) {
      localStorage.setItem('last-activity', Date.now().toString())
    }

    // Auto-refresh token every 20 minutes (only if recently active)
    refreshIntervalRef.current = setInterval(() => {
      const lastActivity = parseInt(localStorage.getItem('last-activity') || '0')
      const timeSinceActivity = Date.now() - lastActivity

      // Only refresh if user has been active in the last 30 minutes
      if (timeSinceActivity < MAX_IDLE_BEFORE_REFRESH) {
        console.log('[SessionManager] User active recently, refreshing token')
        refreshToken()
      } else {
        console.log('[SessionManager] User inactive for', Math.floor(timeSinceActivity / 60000), 'minutes - skipping refresh')
      }
    }, REFRESH_INTERVAL)

    // Check for expiry every 30 seconds
    expiryCheckIntervalRef.current = setInterval(() => {
      checkSessionExpiry()
    }, 30 * 1000)

    // Track user activity (mouse, keyboard, scroll)
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart']
    const handleActivity = () => {
      updateActivity()
    }

    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity)
    })

    // Initial expiry check
    checkSessionExpiry()

    // Cleanup
    return () => {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current)
      if (expiryCheckIntervalRef.current) clearInterval(expiryCheckIntervalRef.current)
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity)
      })
    }
  }, [enabled])

  /**
   * Format time remaining for display
   */
  const formatTimeRemaining = (ms: number): string => {
    const minutes = Math.floor(ms / 60000)
    if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''}`
    const hours = Math.floor(minutes / 60)
    return `${hours} hour${hours !== 1 ? 's' : ''}`
  }

  // Warning Modal (5 minutes before expiration)
  if (showWarning && timeRemaining) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-lg font-medium text-gray-900">
                Session Expiring Soon
              </h3>
              <div className="mt-2 text-sm text-gray-600">
                <p>
                  Your session will expire in <strong>{formatTimeRemaining(timeRemaining)}</strong> due to inactivity.
                </p>
                <p className="mt-2">
                  Would you like to stay logged in?
                </p>
              </div>
              <div className="mt-4 flex gap-3">
                <button
                  onClick={handleExtendSession}
                  className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  Stay Logged In
                </button>
                <button
                  onClick={handleLogout}
                  className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
                >
                  Log Out
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Expired Modal
  if (showExpired) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-lg font-medium text-gray-900">
                Session Expired
              </h3>
              <div className="mt-2 text-sm text-gray-600">
                <p>
                  Your session has expired due to inactivity. Please log in again to continue.
                </p>
              </div>
              <div className="mt-4">
                <button
                  onClick={handleLogout}
                  className="w-full bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  Go to Login
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return null
}

export default SessionManager
